import { LitElement, html, css } from 'lit';
import { Assets } from 'shared/assets';

export class Dialog extends LitElement {
  static styles = css`
    :root {
      --brand-primary: #438BDF;
      --inactive-color: #CCCCCC;
      --danger-color: #D75762;
      --warning-color: #E4B079;
      --info-color: #85E2FE;
      --success-color: #33C494;
      --smart-scan-good-color: #a1e3fc;
      --smart-scan-semi-broken-color: #ffc9b2;
      --smart-scan-broken-color: #c9a6fa;
      --section-box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.5);
      --section-border-radius: 8px;
      --main-font: "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva, Verdana, sans-serif;
    }

    *, *::before, *::after {
      box-sizing: border-box;
    }

    :host {
      display: flex;
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      z-index: 5500;
      background: rgba(0, 0, 0, 0.5);
      overflow: auto;
      justify-content: center;
      align-items: flex-start;
      font-size: 14px;
      font-family: var(--main-font);
    }

    .box {
      margin-top: calc(10vh + 35px);
      background-color: #F5F5F5;
      border: 1px solid #DCDCDC;
      border-radius: var(--section-border-radius);
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
      padding: 25px;
      max-width: 450px;
      font-size: 16px;
      line-height: 22.4px;
      color: #5B5B5B;
      position: relative;
    }

    .modal-header {
      font-size: 14px;
      font-weight: normal;
      line-height: 1em;
      color: #666666;
      border-bottom: 1px solid #979797;
      padding-bottom: 3px;
      margin-bottom: 20px;
      background: transparent;
      border-radius: 0;
      text-align: left;
    }

    slot[name="message"] {
      display: block;
      font-family: var(--main-font);
      font-size: 14px;
      line-height: 1em;
      color: #666666;
      margin: 0 0 20px;
      padding: 0 0 3px 0;
      text-align: left;
    }

    .buttons {
      display: flex;
      justify-content: center;
      align-items: center;
      margin: 24px auto 0;
      text-align: center;
      max-width: 500px;
      font-size: 16px;
      line-height: 22.4px;
      font-weight: 400;
      color: #5B5B5B;
    }

    .btn-auto {
      font-family: var(--main-font);
      font-size: 16px;
      line-height: 1em;
      border-radius: var(--section-border-radius);
      outline: none !important;
      display: inline-block;
      cursor: pointer;
      user-select: none;
      padding: 8px 10px;
      margin: 0 6px;
      width: 190px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
      text-decoration: none;
      transition: background-color 0.2s, color 0.2s;
    }

    .btn-primary {
      background-color: var(--brand-primary);
      border: 1px solid var(--brand-primary);
      color: #FFFFFF;
    }

    .btn-text {
      background-color: #FFFFFF;
      border: 1px solid #979797;
      color: #5B5B5B;
    }
  `;

  static properties = {
    value: { type: String },
    closeCallback: { type: Function },
  };

  constructor() {
    super();
    this.value = 'close';
    this.closeCallback = null;
    this._handleOutsideClick = this._handleOutsideClick.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this._handleOutsideClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._handleOutsideClick);
    super.disconnectedCallback();
  }

  render() {
    return html`
      <div class="box">
        <div class="modal-header">Confirmation Required</div>
        <slot name="message">-Message Missing-</slot>
        <div class="buttons">
          <slot name="button" @click=${this._buttonClick}>
            <a class="btn-primary btn-auto" value="yes">Yes</a>
            <a class="btn-text btn-auto" value="no">No</a>
          </slot>
        </div>
      </div>
    `;
  }

  _handleOutsideClick(event) {
    const box = this.shadowRoot.querySelector('.box');
    const path = event.composedPath();
    if (!path.includes(box)) {
      this.close();
    }
  }

  _buttonClick(event) {
    const val = event.target.getAttribute('value');
    if (val) {
      this.value = val;
    }
    this.close();
  }

  close() {
    this.dispatchEvent(new Event('close', { bubbles: true, composed: true }));
    if (this.closeCallback) {
      this.closeCallback(this.value);
    }
  }

  static open(htmlContent, closeCallback) {
    const element = document.createElement('dialog-box');
    element.innerHTML = htmlContent;
    element.closeCallback = closeCallback;

    const cleanup = () => {
      element.removeEventListener('close', cleanup);
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
    };

    element.addEventListener('close', cleanup);
    document.body.appendChild(element);
    return element;
  }
}

customElements.define('dialog-box', Dialog);
